import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '@/lib/format';

interface LanguageSwitcherProps {
  buttonClassName?: string;
  menuClassName?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ buttonClassName = '', menuClassName = '' }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const lang = normalizeLang(i18n.language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const setLanguage = (next: 'en-US' | 'es-MX') => {
    if (next === lang) {
      setOpen(false);
      return;
    }
    i18n.changeLanguage(next);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t('nav.language')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {lang === 'es-MX' ? 'ES' : 'EN'}
      </button>
      {open && (
        <div
          className={`absolute right-0 mt-2 w-40 rounded-lg border border-[#2f2f2f] bg-[#1f1f1f] shadow-lg shadow-black/30 overflow-hidden fade-soft ${menuClassName}`}
          role="menu"
        >
          <button
            type="button"
            onClick={() => setLanguage('en-US')}
            className="w-full px-3 py-2 text-left text-sm text-[#E0E0E0] hover:bg-[#2a2a2a] transition-colors"
            role="menuitem"
          >
            English (US)
          </button>
          <button
            type="button"
            onClick={() => setLanguage('es-MX')}
            className="w-full px-3 py-2 text-left text-sm text-[#E0E0E0] hover:bg-[#2a2a2a] transition-colors"
            role="menuitem"
          >
            Espanol (MX)
          </button>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
